import PropTypes from 'prop-types';
import './SimpleTable.css';
import copyIcon from '../../images/copyIcon.png';
import { ReactComponent as OptionsIcon } from '../../images/3dots.svg';
import OverLayWrapper from '../Overlay';
import { TriangleExclamation } from '@styled-icons/fa-solid';

const handleCopyRef = (text) => {
  navigator.clipboard.writeText(text);
};

const RenderLabel = ({ label, copyValue, copyButtonId }) => {
  return (
    <>
      {label}
      {copyValue && (
        <button
          data-testid={copyButtonId}
          onClick={() => handleCopyRef(copyValue)}
          title="Copy"
          className="btn btn-link p-0 simple-table-copy-button"
        >
          <img src={copyIcon} alt="copy icon" />
        </button>
      )}
    </>
  );
};

const RenderValue = ({ value, onClick, textAlign, disabled, kebabMenuId, truncate }) => {
  return (
    <>
      <div
        className={`w-100 ${textAlign === 'right' ? 'text-end' : ''} ${truncate ? 'simple-table-truncate-cell' : ''}`}
        title={truncate ? value : undefined}
      >
        {value}
      </div>
      {onClick && !disabled && (
        <div className="simple-table-options-button">
          <OptionsIcon data-testid={kebabMenuId} onClick={onClick} />
        </div>
      )}
    </>
  );
};

const RenderHeading = ({ heading, headerBackground }) => {
  const hasError = heading.error && !heading.disabled;
  const hasWarning = heading.warning && !heading.disabled;

  return heading?.errorMessage ? (
    <OverLayWrapper placement="top" popoverContent={<p>{heading.errorMessage}</p>}>
      <div
        className={`col ${hasError && 'simple-table-error'} ${hasWarning && 'simple-table-warning'}`}
        style={headerBackground ? { backgroundColor: headerBackground } : undefined}
      >
        <RenderLabel label={heading.label} copyValue={heading.copyValue} copyButtonId={heading.copyButtonId} />
        {heading?.showErrorIcon && <TriangleExclamation width="1rem" height="1rem" />}
      </div>
    </OverLayWrapper>
  ) : (
    <div
      className={`col ${hasError && 'simple-table-error'} ${hasWarning && 'simple-table-warning'}`}
      style={headerBackground ? { backgroundColor: headerBackground } : undefined}
    >
      <RenderLabel label={heading.label} copyValue={heading.copyValue} copyButtonId={heading.copyButtonId} />
    </div>
  );
};

const SimpleTable = ({
  data,
  isHeaderRow = true,
  textAlign = 'left',
  headerBackground,
  cellBackground,
  dataTestIdPrefix,
}) => {
  const { headings, values } = data;

  if (!headings || !values) {
    return null;
  }

  const visibleHeadings = headings.filter((h) => !h.hide);

  // Check if any heading has a size property
  const hasSizeProperty = headings.some((h) => h.size !== undefined);

  // Calculate grid template columns if size property exists
  const gridTemplateColumns = hasSizeProperty ? visibleHeadings.map((h) => `${h.size || 1}fr`).join(' ') : undefined;

  if (!isHeaderRow) {
    // headings should be in the first column
    return (
      <div className="simple-table header-is-column container-fluid" data-testid="simple-table">
        {visibleHeadings.map((heading) => {
          return (
            <div className={`row ${heading.disabled && 'disabled'}`} key={heading.key}>
              <RenderHeading heading={heading} headerBackground={headerBackground} />
              {values.map((value) => {
                return (
                  <div
                    data-testid={heading.valueCellId || undefined}
                    className="col"
                    key={`${heading.key}-${value[heading.key]}`}
                    style={cellBackground ? { backgroundColor: cellBackground } : undefined}
                  >
                    <RenderValue
                      value={value[heading.key]}
                      onClick={heading.onClick}
                      textAlign={textAlign}
                      disabled={heading.disabled}
                      kebabMenuId={heading.kebabMenuId}
                      truncate={heading.truncate}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="simple-table header-is-row container-fluid">
      <div
        className={gridTemplateColumns ? '' : 'row'}
        style={
          gridTemplateColumns
            ? { display: 'grid', gridTemplateColumns, borderBottom: '1px solid var(--qa-table-border-color)' }
            : undefined
        }
      >
        {visibleHeadings.map((heading) => (
          <div
            className={`col ${heading.error && 'simple-table-error'} ${heading.warning && 'simple-table-warning'}`}
            key={heading.key}
            style={{
              ...(headerBackground ? { backgroundColor: headerBackground } : {}),
              ...(gridTemplateColumns && heading.textAlign
                ? {
                    justifyContent: heading.textAlign === 'right' ? 'flex-end' : 'flex-start',
                    textAlign: heading.textAlign,
                  }
                : {}),
            }}
          >
            <RenderLabel label={heading.label} copyValue={heading.copyValue} copyButtonId={heading.copyButtonId} />
          </div>
        ))}
      </div>
      {values.map((value, rowIndex) => (
        <div
          className={gridTemplateColumns ? '' : 'row'}
          key={value.id || value.item}
          style={
            gridTemplateColumns
              ? { display: 'grid', gridTemplateColumns, borderBottom: '1px solid var(--qa-table-border-color)' }
              : undefined
          }
        >
          {visibleHeadings.map((heading, colIndex) => (
            <div
              data-testid={
                dataTestIdPrefix
                  ? `${dataTestIdPrefix}-row${rowIndex}-col${colIndex}`
                  : heading.valueCellId || undefined
              }
              className="col"
              key={'H' + heading.key}
              style={{
                ...(cellBackground ? { backgroundColor: cellBackground } : {}),
                ...(gridTemplateColumns && heading.textAlign
                  ? {
                      justifyContent: heading.textAlign === 'right' ? 'flex-end' : 'flex-start',
                      textAlign: heading.textAlign,
                    }
                  : {}),
              }}
            >
              <RenderValue
                value={value[heading.key]}
                onClick={heading.onClick}
                textAlign={heading.textAlign || textAlign}
                kebabMenuId={heading.kebabMenuId}
                truncate={heading.truncate}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

SimpleTable.propTypes = {
  data: PropTypes.shape({
    headings: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        hide: PropTypes.bool,
        error: PropTypes.bool,
        warning: PropTypes.bool,
        copyValue: PropTypes.string,
        copyButtonId: PropTypes.string,
        onClick: PropTypes.func,
        size: PropTypes.number,
        textAlign: PropTypes.oneOf(['left', 'right']),
        valueCellId: PropTypes.string,
        kebabMenuId: PropTypes.string,
        truncate: PropTypes.bool,
      })
    ),
    values: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  isHeaderRow: PropTypes.bool,
  textAlign: PropTypes.oneOf(['left', 'right']),
  headerBackground: PropTypes.string,
  cellBackground: PropTypes.string,
  dataTestIdPrefix: PropTypes.string,
};

RenderLabel.propTypes = {
  label: PropTypes.node.isRequired,
  copyValue: PropTypes.string,
  copyButtonId: PropTypes.string,
};

RenderValue.propTypes = {
  value: PropTypes.node,
  onClick: PropTypes.func,
  kebabMenuId: PropTypes.string,
  truncate: PropTypes.bool,
};

export default SimpleTable;
